import React, { useMemo, useRef } from 'react';
import { Position, BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import dagre from 'dagre';
import BhHandle from 'components/workflow/generic/flowkit/handle';
import { BhFlowNode, BhFlowTitle, BhFlowSubtitle } from 'components/workflow/generic/flowkit/flowNode';
import { edgeStrokeStyle } from 'components/workflow/generic/flowkit/flowEdge';
import { reconcileElements } from 'components/workflow/generic/flowkit/reconcile';
import WidgetStatus from '../../../common/widgetStatus';
import GraphCanvas from '../../components/graphCanvas';

const NODE_H = 52;
// Reserved routing endpoints. They carry no id in `nodes`, but we DO render them as (distinct) sentinel
// nodes so @start → … → @end reads as a complete flow.
const START = '@start';
const END = '@end';

// Auto width: size each node to its label so long step ids aren't clipped and short ones aren't padded.
// A cheap character estimate (no DOM measuring) is enough to keep the dagre layout overlap-free, because
// the rendered node is pinned to this same width.
function nodeWidth(...texts) {
  const len = Math.max(0, ...texts.map(t => String(t || '').length));
  return Math.round(Math.min(280, Math.max(72, len * 8 + 34)));
}

// Read-only flow-kit node for one workflow step / sentinel: the id only, with a left TARGET handle
// (incoming) and a right SOURCE handle (outgoing) so left-to-right edges anchor to it.
function WorkflowStepNode({ id, data, selected }) {
  const isSentinel = !!(data && data.sentinel);
  const attachments = (
    <>
      <BhHandle mode="fixed" type="target" position={Position.Left} id="in" isConnectable={false} />
      <BhHandle mode="fixed" type="source" position={Position.Right} id="out" isConnectable={false} />
    </>
  );
  return (
    <BhFlowNode
      selected={selected}
      className={`mc-workflow-step-node${isSentinel ? ' mc-workflow-sentinel-node' : ''}`}
      attachments={attachments}
    >
      <BhFlowTitle>{(data && data.label) || id}</BhFlowTitle>
      {data && data.type && <BhFlowSubtitle>{data.type}</BhFlowSubtitle>}
    </BhFlowNode>
  );
}

const NODE_TYPES = { workflowStep: WorkflowStepNode };

// Own elongated arrowhead. ReactFlow's built-in ArrowClosed is squat AND its square viewBox preserves
// aspect ratio, so widening the marker alone never lengthens it — hence a real 2:1 marker here (same
// shape as the mission edge's, just longer).
const WF_ARROW_ID = 'mc-wf-edge-arrow';

function WorkflowEdgeMarkers() {
  return (
    <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
      <defs>
        <marker
          id={WF_ARROW_ID}
          markerWidth="16"
          markerHeight="8"
          viewBox="0 0 16 8"
          orient="auto"
          refX="16"
          refY="4"
          markerUnits="userSpaceOnUse"
        >
          <path d="M0,0 L16,4 L0,8 Z" fill="#9aa3af" />
        </marker>
      </defs>
    </svg>
  );
}

// A rounded polyline through dagre's routed points, so the edge follows the layout AROUND the nodes
// instead of a straight bezier that cuts through them. Corners are softened with short quadratic arcs.
function pointsToPath(pts, radius = 8) {
  if (!pts || pts.length < 2) return '';
  if (pts.length === 2) return `M ${pts[0].x},${pts[0].y} L ${pts[1].x},${pts[1].y}`;
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const next = pts[i + 1];
    const clamp = (from) => {
      const dx = curr.x - from.x;
      const dy = curr.y - from.y;
      const len = Math.hypot(dx, dy) || 1;
      const r = Math.min(radius, len / 2);
      return { x: curr.x - (dx / len) * r, y: curr.y - (dy / len) * r };
    };
    const a = clamp(prev);
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;
    const len2 = Math.hypot(dx2, dy2) || 1;
    const r2 = Math.min(radius, len2 / 2);
    const b = { x: curr.x + (dx2 / len2) * r2, y: curr.y + (dy2 / len2) * r2 };
    d += ` L ${a.x},${a.y} Q ${curr.x},${curr.y} ${b.x},${b.y}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
}

// Edge that draws dagre's routed polyline (data.points) with the arrowhead + optional `when` label.
function WorkflowEdge({ id, data, style }) {
  const pts = (data && data.points) || [];
  const path = pointsToPath(pts);
  if (!path) return null;
  const mid = pts[Math.floor((pts.length - 1) / 2)] || pts[0];
  const label = data && data.label;
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={`url(#${WF_ARROW_ID})`} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${mid.x}px, ${mid.y}px)`,
              fontSize: 11,
              color: '#5b6572',
              background: '#fff',
              padding: '0 3px',
              borderRadius: 3,
              pointerEvents: 'none',
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const EDGE_TYPES = { workflowEdge: WorkflowEdge };

/**
 * What the drawing depends on, as one comparable string.
 *
 * The workflow arrives as the cell's EMBEDDED copy, and the store rebuilds itself from the network on
 * every session event — so an untouched cell hands this a fresh-but-equal object several times a
 * minute. Keyed on that object's IDENTITY, the whole element set was rebuilt each time; ReactFlow then
 * re-measures every node it has never seen before, and the frame before it has measured them is the
 * flicker. Keyed on the content, an unchanged workflow re-renders the same objects and nothing moves.
 *
 * Everything the builder reads goes in — step ids and types, each edge's endpoints and `when`, and the
 * highlighted step, which is a node's `selected`.
 */
export function workflowSignature(workflow, currentStep) {
  const nodes = (workflow && Array.isArray(workflow.nodes)) ? workflow.nodes : [];
  const edges = (workflow && Array.isArray(workflow.edges)) ? workflow.edges : [];
  return [
    nodes.map(n => `${n && n.id}:${(n && (n.ty || n.type)) || ''}`).join(','),
    edges.map(e => `${e && e.from}->${e && e.to}:${(e && e.when) || ''}`).join(','),
    currentStep || '',
  ].join('#');
}

/**
 * WorkflowGraph — read-only flow-kit rendering of a cell workflow. Real steps (labeled by their step id,
 * with the step type as a small muted second line) plus the @start / @end sentinels are laid out
 * left-to-right (dagre); each edge follows dagre's routed polyline AROUND intermediate nodes (not a
 * straight bezier through them) and is the workflow's from→to
 * routing with a direction arrowhead and its optional `when` label. Nodes auto-size to their text and the
 * layout is fed those widths so nothing overlaps. Uses the shared canvas (GraphCanvas) + flow-kit styling;
 * zoom is by mouse wheel (no on-canvas zoom controls).
 *
 * Props:
 *   workflow    — { nodes: [{ id, ... }], edges: [{ from, to, when? }] }
 *   currentStep — optional step id to highlight (the cell's active step)
 */
export default function WorkflowGraph({ workflow, currentStep }) {
  const signature = workflowSignature(workflow, currentStep);
  // What is on screen, so a real change can be merged onto it instead of replacing it — the step mark
  // moving is news about two nodes, and the rest keep what ReactFlow measured (flowkit/reconcile.js).
  const shown = useRef({ rfNodes: [], rfEdges: [] });
  const { rfNodes, rfEdges, hasContent } = useMemo(() => {
    const steps = (workflow && Array.isArray(workflow.nodes))
      ? workflow.nodes.filter(n => n && n.id && n.id !== START && n.id !== END)
      : [];
    const wfEdges = (workflow && Array.isArray(workflow.edges)) ? workflow.edges.filter(Boolean) : [];

    // Node set = real steps + whichever sentinels the edges actually reference.
    const referenced = new Set();
    wfEdges.forEach(e => { referenced.add(e.from); referenced.add(e.to); });
    const nodeDefs = steps.map(n => ({ id: n.id, label: n.id, sentinel: false, type: n.ty || n.type || null }));
    if (referenced.has(START)) nodeDefs.push({ id: START, label: START, sentinel: true });
    if (referenced.has(END)) nodeDefs.push({ id: END, label: END, sentinel: true });

    const nodeIds = new Set(nodeDefs.map(n => n.id));
    // One edge per (from,to,when) tuple, both endpoints present.
    const seen = new Set();
    const edgeDefs = wfEdges.filter(e => {
      if (!nodeIds.has(e.from) || !nodeIds.has(e.to)) return false;
      const k = `${e.from}->${e.to}:${e.when || ''}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    // dagre LR layout, fed each node's real (auto) width so ranks/rows never overlap.
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 96, marginx: 18, marginy: 18 });
    g.setDefaultEdgeLabel(() => ({}));
    const widthOf = {};
    for (const n of nodeDefs) {
      const w = nodeWidth(n.label, n.type);
      widthOf[n.id] = w;
      g.setNode(n.id, { width: w, height: NODE_H });
    }
    for (const e of edgeDefs) g.setEdge(e.from, e.to);
    dagre.layout(g);

    const rfNodes = nodeDefs.map(n => {
      const gn = g.node(n.id);
      const w = widthOf[n.id];
      return {
        id: n.id,
        type: 'workflowStep',
        position: gn ? { x: gn.x - w / 2, y: gn.y - NODE_H / 2 } : { x: 0, y: 0 },
        selected: n.id === currentStep,
        draggable: false,
        data: { label: n.label, sentinel: n.sentinel, type: n.type },
        style: { width: w },
      };
    });

    const rfEdges = edgeDefs.map((e, i) => {
      const de = g.edge(e.from, e.to);
      return {
        id: `we-${i}-${e.from}-${e.to}`,
        source: e.from,
        target: e.to,
        sourceHandle: 'out',
        targetHandle: 'in',
        type: 'workflowEdge',
        style: edgeStrokeStyle(false),
        // dagre's routed points make the edge follow the layout around intermediate nodes.
        data: { points: (de && de.points) || [], label: e.when || undefined },
      };
    });

    shown.current = {
      rfNodes: reconcileElements(shown.current.rfNodes, rfNodes),
      rfEdges: reconcileElements(shown.current.rfEdges, rfEdges),
    };
    return { ...shown.current, hasContent: nodeDefs.length > 0 };
    // Keyed on the CONTENT, not on the objects carrying it — see workflowSignature above.
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasContent) {
    return <WidgetStatus variant="light">No steps.</WidgetStatus>;
  }

  return (
    <div className="mc-workflow-graph">
      <WorkflowEdgeMarkers />
      <GraphCanvas
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        fitView
        selectable={false}
        attribution={false}
        showControls={false}
        showBackground={false}
        fitViewOptions={{ maxZoom: 0.8, padding: 0.16 }}
        minZoom={0.2}
      />
    </div>
  );
}
